import {
    actionEffect,
    ListenerMiddleware,
    predicateEffect,
    registerActionEffect,
    registerPredicateEffect,
} from 'store/listenerMiddleware';
import { isAnyOf } from '@reduxjs/toolkit';
import {
    savedPreference,
    selectUserPreference,
} from 'userPreferences/userPreferencesSlice';
import {
    IndexMapping,
    IndexMappingsUserPreferenceKey,
} from './IndexMappingsState';
import { actions, selectIndexMappingsInSymbology } from './IndexMappingsSlice';
import { selectCurrentSymbology } from 'portfolio/store/selectors';
import State from 'store/State';
import { AnyAction } from 'store/store';
import { SymbologyValues } from 'app/store/appSettings/AppSettingsState';
import { buildLogger } from '@instinet-analytics/core-lib';

export const checkStreamsOnIndexMappingUserPreferenceSaveEffect = actionEffect(
    isAnyOf(savedPreference),
    async (action, dispatch, state, services) => {
        const { key } = action.payload;
        const { widgetStreamingService } = services;
        if (key === IndexMappingsUserPreferenceKey) {
            widgetStreamingService.checkAllStreams('Index Mappings Changed');
        }
    }
);

export const convertRetrievedIndexMappingsToUsersSymbologyEffect =
    predicateEffect(
        (action: AnyAction, currentState: State, previousState: State) => {
            const currentValue = selectUserPreference(
                currentState,
                IndexMappingsUserPreferenceKey
            );
            const previousValue = selectUserPreference(
                previousState,
                IndexMappingsUserPreferenceKey
            );
            // This is a first time setting of the user pref after a retrieval so we may need to convert...
            return currentValue !== undefined && previousValue === undefined;
        },
        async (action, api) => {
            const {
                getState,
                extra: { symbolsService, loggingService },
                dispatch,
            } = api;

            const state = getState();

            const userPrefMappings = selectUserPreference<
                IndexMapping[] | undefined
            >(state, IndexMappingsUserPreferenceKey);
            const symbology = selectCurrentSymbology(state);

            dispatch(
                actions.setMappings({
                    symbology: SymbologyValues.Instinet,
                    mappings: userPrefMappings ?? [],
                })
            );

            if (symbology === SymbologyValues.Instinet) {
                // Retrieved mappings use the Instinet symbology...
                return;
            }

            const logger = buildLogger(
                'convertRetrievedIndexMappingsToUsersSymbologyEffect',
                loggingService
            );
            logger.debug(
                `Converting index mappings to ${symbology} following user preference retrieval`
            );

            symbolsService
                .convertIndexMappingsSymbologies(
                    userPrefMappings,
                    SymbologyValues.Instinet,
                    symbology
                )
                .then((converted) => {
                    logger.debug(`Converting index mappings complete`, {
                        converted,
                        symbology,
                    });
                    if (converted) {
                        dispatch(
                            actions.setMappings({
                                symbology,
                                mappings: converted,
                            })
                        );
                    }
                });
        }
    );

export const convertIndexMappingsOnSymbologyChangeEffect = predicateEffect(
    (action: AnyAction, currentState: State, previousState: State) => {
        const nextSymbology = selectCurrentSymbology(currentState);
        const previousSymbology = selectCurrentSymbology(previousState);
        return nextSymbology !== previousSymbology;
    },
    async (action, api) => {
        const {
            getState,
            getOriginalState,
            extra: { symbolsService, loggingService },
            dispatch,
        } = api;

        const state = getState();
        const previousState = getOriginalState();

        const nextSymbology = selectCurrentSymbology(state);
        const previousSymbology = selectCurrentSymbology(previousState);

        if (previousSymbology !== SymbologyValues.Instinet) {
            // Avoid stale values
            dispatch(
                actions.removeMappings({
                    symbology: previousSymbology,
                })
            );
        }

        if (nextSymbology === SymbologyValues.Instinet) {
            // No need to convert...
            return;
        }

        const indexMappingsInstinetSymbology = selectIndexMappingsInSymbology(
            state,
            SymbologyValues.Instinet
        );

        if (!indexMappingsInstinetSymbology) {
            // They have not been retrieved yet so no need to do anything...
            return;
        }

        const indexMappingsNextSymbology = selectIndexMappingsInSymbology(
            state,
            nextSymbology
        );

        if (indexMappingsNextSymbology) {
            // Already have them...
            return;
        }

        const logger = buildLogger(
            'convertIndexMappingsOnSymbologyChangeEffect',
            loggingService
        );
        logger.debug(
            `Converting index mappings to ${nextSymbology} following user symbology change`
        );

        symbolsService
            .convertIndexMappingsSymbologies(
                indexMappingsInstinetSymbology,
                SymbologyValues.Instinet,
                nextSymbology
            )
            .then((converted) => {
                logger.debug(`Converting index mappings complete`, {
                    converted,
                    nextSymbology,
                });
                if (converted) {
                    dispatch(
                        actions.setMappings({
                            symbology: nextSymbology,
                            mappings: converted,
                        })
                    );
                }
            });
    }
);

export function registerIndexMappingEffects(middleware: ListenerMiddleware) {
    registerActionEffect(
        middleware,
        checkStreamsOnIndexMappingUserPreferenceSaveEffect
    );
    registerPredicateEffect(
        middleware,
        convertRetrievedIndexMappingsToUsersSymbologyEffect
    );
    registerPredicateEffect(
        middleware,
        convertIndexMappingsOnSymbologyChangeEffect
    );
}
